/** Header-action builders for the RFI Register, in a project's RFI Register
 *  tab and in System Settings. The list gets the status filter (with counts)
 *  and New RFI; the form gets Download PDF, Save and Issue RFI. Search is the
 *  header's persistent desktop field (getActiveSearchConfig), not a pill.
 *  Every pill fires a window event RFIManager or RFIForm listens for.
 *
 *  Words are desktop-only (desktopLabel): a phone's title is centred between
 *  the two clusters, so a worded pill there squeezes the title out. No JSX. */

import { Check, FileDown, Loader2, Plus, Save, Send, SlidersHorizontal } from '@/shared/ui/icons';
import {
  RFI_CREATE_EVENT,
  RFI_FORM_EXPORT_EVENT,
  RFI_FORM_ISSUE_EVENT,
  RFI_FORM_SAVE_EVENT,
  RFI_SET_FILTER_EVENT,
  RFI_STATUS_FILTERS,
  type RfiFormHeaderState,
  type RfiRegisterHeaderState,
} from '../../../features/templates/rfiRegister';
import type { MobileHeaderAction } from './types';

export function buildRfiRegisterHeaderActions(ctx: {
  state: RfiRegisterHeaderState;
  trailingActions: MobileHeaderAction[];
}): void {
  const { state, trailingActions } = ctx;
  const active = RFI_STATUS_FILTERS.find((filter) => filter.id === state.statusFilter);
  const narrowed = state.statusFilter !== 'all';

  // Nothing to filter in an empty register; New RFI is all it needs.
  if (state.counts.all > 0) {
    trailingActions.push({
      id: 'rfi-filter',
      icon: SlidersHorizontal,
      // Labelled only while narrowed, so the resting header stays quiet and a
      // filtered register is impossible to mistake for the whole one.
      desktopLabel: narrowed && active ? `${active.label} ${state.counts[active.id]}` : undefined,
      ariaLabel: 'Filter RFIs by status',
      active: narrowed,
      group: 'view',
      subActions: RFI_STATUS_FILTERS.map((filter) => ({
        id: `rfi-filter-${filter.id}`,
        label: `${filter.label} (${state.counts[filter.id]})`,
        active: state.statusFilter === filter.id,
        trailingIcon: state.statusFilter === filter.id ? Check : undefined,
        onClick: () => window.dispatchEvent(new CustomEvent(RFI_SET_FILTER_EVENT, { detail: { filter: filter.id } })),
      })),
    });
  }
  trailingActions.push({
    id: 'rfi-create',
    icon: Plus,
    desktopLabel: 'New RFI',
    ariaLabel: 'New RFI',
    group: 'create',
    onClick: () => window.dispatchEvent(new CustomEvent(RFI_CREATE_EVENT)),
  });
}

export function buildRfiFormHeaderActions(ctx: {
  state: RfiFormHeaderState;
  trailingActions: MobileHeaderAction[];
}): void {
  const { state, trailingActions } = ctx;
  const busy = state.saving !== null;

  if (state.canExport) {
    trailingActions.push({
      id: 'rfi-form-export',
      icon: FileDown,
      ariaLabel: 'Download PDF',
      disabled: busy,
      group: 'output',
      onClick: () => window.dispatchEvent(new CustomEvent(RFI_FORM_EXPORT_EVENT)),
    });
  }
  trailingActions.push({
    id: 'rfi-form-save',
    icon: state.saving === 'save' ? Loader2 : Save,
    desktopLabel: state.saving === 'save' ? 'Saving…' : state.saveLabel,
    ariaLabel: state.saveLabel,
    disabled: busy,
    group: 'page',
    onClick: () => window.dispatchEvent(new CustomEvent(RFI_FORM_SAVE_EVENT)),
  });
  if (state.canIssue) {
    trailingActions.push({
      id: 'rfi-form-issue',
      icon: state.saving === 'issue' ? Loader2 : Send,
      desktopLabel: state.saving === 'issue' ? 'Recording…' : 'Record as issued',
      ariaLabel: 'Record RFI as issued',
      disabled: busy,
      group: 'progress',
      onClick: () => window.dispatchEvent(new CustomEvent(RFI_FORM_ISSUE_EVENT)),
    });
  }
}
